/**
 * Super Reversi - Complete Game Implementation with Particle Effects
 *
 * Full working version with game logic, AI, animations, and particle effects
 */

import './style.css';
import './main-complete.css';
import { ParticleSystem, type Grade, type SpecialEffect } from './particle-system';
import { t, setLanguage, applyTranslations, type Language } from './i18n/index';
import { ComboSystem, type ComboEvent } from './combos/combo-system';
import { SpecialMoveDetector } from './effects/special-moves';
import { UIManager } from './ui/ui-manager';
import { ScreenType, MenuAction } from './types/ui-types';
import { ModalService } from './ui/modal-service';
import { defaultAIManager, type AIMoveRequest } from './core/ai/ai-strategy-manager';
import { AIDifficulty, PlayerColor } from './types/game-types';
import type { GameState as CoreGameState } from './core/models/GameState';

// Constants
const BOARD_SIZE = 8;
const EMPTY = 0;
const BLACK = 1;
const WHITE = 2;
const DIRECTIONS: [number, number][] = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1],
];

// Game State
interface GameState {
  board: number[];
  currentPlayer: number;
  blackScore: number;
  whiteScore: number;
  gameMode: 'pvp' | 'pvc';
  isGameOver: boolean;
  validMoves: Set<number>;
  moveHistory: Array<{position: number, player: number, flipped: number[]}>;
  aiDifficulty: AIDifficulty;
  consecutivePasses?: number;
}

class SuperReversiGame {
  private container: HTMLElement | null = null;
  private currentScreen: 'menu' | 'game' | 'help' | 'settings' | 'difficulty' = 'menu';
  private selectedAI: AIDifficulty | null = null;
  private customSettings: any = null;
  private gameState: GameState | null = null;
  private isProcessingMove = false;
  private aiThinking = false;
  private particleSystem: ParticleSystem;
  private comboSystem: ComboSystem;
  private uiManager: UIManager | null = null;
  private modalService: ModalService | null = null;

  // 戰鬥UI元素
  private comboDisplay: HTMLElement | null = null;
  private specialMoveDisplay: HTMLElement | null = null;

  constructor() {
    console.log('🎮 Super Reversi Battle Enhanced - Initializing...');
    this.particleSystem = new ParticleSystem();
    this.comboSystem = new ComboSystem();

    // 監聽連擊事件
    this.comboSystem.addEventListener((comboEvent: ComboEvent) => {
      this.handleComboEvent(comboEvent);
    });
  }

  private showElement(element: HTMLElement | null): void {
    if (!element) {return;}
    element.classList.remove('is-hidden');
  }

  private hideElement(element: HTMLElement | null): void {
    if (!element) {return;}
    element.classList.add('is-hidden');
  }

  async initialize(container: HTMLElement): Promise<void> {
    this.container = container;
    const settings = this.getSettings();
    const lang = (settings.language as Language) || 'en';
    setLanguage(lang);
    applyTranslations();
    this.setupUI();

    // Initialize UI Manager and Modal Service - commented out to avoid conflicts with setupUI
    // this.uiManager = new UIManager();
    // this.uiManager.initialize(container);
    // this.modalService = new ModalService(this.uiManager);

    // Initialize particle system
    this.particleSystem.initialize(container);

    // Set particle system enabled state based on settings
    this.particleSystem.setEnabled(settings.animations);

    this.showMenu();
    console.log('✅ Super Reversi ready!');
  }

  private setupUI(): void {
    if (!this.container) {return;}

    this.container.innerHTML = `
      <div class="super-reversi-app">
        <div class="game-header" id="game-header">
          <button class="header-back-btn is-hidden" id="header-back-btn">⬅️</button>
          <h1 data-i18n="app.title">${t('app.title')}</h1>
        </div>

        <!-- Shared Health Bar System -->
        <div class="shared-health-bar-container is-hidden" id="shared-health-bar-container">
          <!-- Left Player Section (Black) -->
          <div class="player-section left">
            <div class="player-info">
              <div class="player-icon">⚫</div>
              <div class="player-name">${t('game.black')}</div>
            </div>
            <div class="piece-count" id="black-count">2</div>
          </div>

          <!-- Shared Health Bar -->
          <div class="shared-health-bar">
            <div class="health-fill-black" id="black-fill"></div>
            <div class="health-fill-white" id="white-fill"></div>
            <div class="vs-divider" id="vs-divider">
              <span class="vs-text">VS</span>
              <div class="turn-indicator" id="turn-indicator-shared">
                <span id="current-turn-shared">${t('game.black')}</span>
              </div>
            </div>
          </div>

          <!-- Right Player Section (White) -->
          <div class="player-section right">
            <div class="piece-count" id="white-count">2</div>
            <div class="player-info">
              <div class="player-name">${t('game.white')}</div>
              <div class="player-icon">⚪</div>
            </div>
          </div>

          <!-- Damage Numbers Container -->
          <div class="damage-numbers" id="damage-numbers"></div>
        </div>
        <!-- 戰鬥UI覆蓋層 -->
        <div class="battle-overlay" id="battle-overlay">
          <div id="combo-display" class="combo-display hidden">
            <div class="combo-count"></div>
            <div class="combo-text"></div>
            <div class="combo-multiplier"></div>
          </div>
          <div id="special-move-display" class="special-move-display hidden">
            <div class="special-move-title"></div>
            <div class="special-move-subtitle"></div>
          </div>
        </div>

        <div id="screen-container" class="screen-container">
          <!-- Screens render here -->
        </div>

        <!-- Simple Modal -->
        <div id="simple-modal" class="modal-backdrop is-hidden">
          <div class="dialog modal">
            <div class="dialog-content">
              <h3 class="dialog-title" id="modal-title" data-i18n="modal.confirm.title">${t('modal.confirm.title')}</h3>
              <p class="dialog-message" id="modal-message" data-i18n="messages.quitConfirm">${t('messages.quitConfirm')}</p>
              <div class="dialog-buttons">
                <button class="dialog-btn primary" id="modal-confirm" data-i18n="modal.confirm">${t('modal.confirm')}</button>
                <button class="dialog-btn secondary" id="modal-cancel" data-i18n="modal.cancel">${t('modal.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // 緩存戰鬥UI元素
    this.comboDisplay = this.container.querySelector('#combo-display');
    this.specialMoveDisplay = this.container.querySelector('#special-move-display');

    // 設置統一返回按鈕事件
    const headerBackBtn = document.getElementById('header-back-btn');
    if (headerBackBtn) {
      headerBackBtn.addEventListener('click', () => {
        if (this.currentScreen === 'game') {
          this.showSimpleConfirm(
            t('messages.quitConfirm'),
            t('modal.quitConfirm.title'),
          ).then(confirmed => {
            if (confirmed) {
              // Clean up AI state before showing menu
              this.cleanupGameState();
              this.showMenu();
            }
          });
        } else {
          this.showMenu();
        }
      });
    }


    // Setup header button event listeners
    this.setupHeaderEvents();
  }

  private setupHeaderEvents(): void {
    // Header settings button
    document.getElementById('header-settings-btn')?.addEventListener('click', () => {
      this.showSettings();
    });

    // Header back button
    document.getElementById('header-back-btn')?.addEventListener('click', () => {
      this.handleBackButton();
    });
  }

  private async handleBackButton(): Promise<void> {
    // Handle back button based on current screen
    switch (this.currentScreen) {
      case 'settings':
      case 'help':
        this.showMenu();
        break;
      case 'difficulty':
        this.showMenu();
        break;
      case 'game':
        // Show confirmation before leaving game
        if (this.gameState && !this.gameState.isGameOver) {
          const confirmed = await this.showSimpleConfirm(
            t('messages.leaveGame')
          );
          if (confirmed) {
            // Clean up AI state before showing menu
            this.cleanupGameState();
            this.showMenu();
          }
        } else {
          // Clean up AI state before showing menu
          this.cleanupGameState();
          this.showMenu();
        }
        break;
      default:
        this.showMenu();
        break;
    }
  }

  /**
   * 處理連擊事件
   */
  private handleComboEvent(event: ComboEvent): void {
    console.log(`🔥 戰鬥: 連擊事件 - ${event.type}, 計數: ${event.comboCount}, 玩家: ${event.player}`);

    if (event.type === 'combo_started' || event.type === 'combo_continued') {
      // 🎆 顯示視覺化連擊UI
      this.showComboUI(event);

      // 顯示舊版訊息作為備用
      this.showMessage(`🔥 ${event.comboCount} 連擊！`);

      // 觸發螢幕中央鏈鎖效果
      if (this.container) {
        const rect = this.container.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        this.particleSystem.createChainEffect(centerX, centerY);
      }
    }
  }

  /**
   * 清理遊戲狀態和AI相關標誌
   */
  private cleanupGameState(): void {
    // Reset AI state
    this.aiThinking = false;
    this.isProcessingMove = false;

    // Stop any ongoing AI thinking
    if (defaultAIManager.isThinking()) {
      console.log('🛑 Stopping AI Manager during cleanup');
      defaultAIManager.stopThinking();
    }

    // Clear AI status display
    const aiStatus = document.getElementById('ai-status');
    if (aiStatus) {
      aiStatus.textContent = '';
    }

    // Reset game state if needed
    if (this.gameState) {
      this.gameState = null;
    }

    console.log('🧹 Game state cleaned up');
  }

  /**
   * 顯示視覺化連擊UI
   */
  private showComboUI(event: ComboEvent): void {
    if (!this.comboDisplay) {return;}

    const { comboCount: count, player, tier, effect } = event;
    console.log(`🔥 UI: 顯示連擊UI - ${count} 連擊 (${tier.id}) for player ${player}`);

    const countElement = this.comboDisplay.querySelector('.combo-count') as HTMLElement;
    const textElement = this.comboDisplay.querySelector('.combo-text') as HTMLElement;
    const multiplierElement = this.comboDisplay.querySelector('.combo-multiplier') as HTMLElement;

    if (countElement) {countElement.textContent = count.toString();}
    if (textElement) {
      const translated = tier.labelKey ? t(tier.labelKey) : '';
      textElement.textContent = translated && translated !== tier.labelKey ? translated : tier.id;
    }
    // 隱藏倍率顯示
    if (multiplierElement) {multiplierElement.classList.add('is-hidden');}

    const color = tier.displayColor ?? '#ffd700';
    this.comboDisplay.style.background = `linear-gradient(135deg, ${color}, ${color}aa)`;

    if (effect && effect.slowMotion) {
      this.comboDisplay.classList.add('combo-slow-motion');
    } else {
      this.comboDisplay.classList.remove('combo-slow-motion');
    }

    // 顯示動畫
    this.comboDisplay.classList.remove('hidden');
    this.comboDisplay.classList.add('combo-animation');

    // 3秒後隱藏
    setTimeout(() => {
      this.comboDisplay?.classList.add('hidden');
      this.comboDisplay?.classList.remove('combo-animation');
    }, 3000);
  }

  /**
   * 檢測並處理特殊招式
   */
  private detectSpecialMoves(position: number, flippedPositions: number[], currentScore: number, opponentScore: number): void {
    console.log(`🔍 檢測特殊招式: 位置=${position}, 翻轉=${flippedPositions.length}個, 分數差=${currentScore - opponentScore}`);

    const specialMoves = SpecialMoveDetector.detectSpecialMove(
      position,
      flippedPositions, // 傳遞實際的翻轉位置陣列
      currentScore,
      opponentScore,
      this.gameState?.moveHistory.length || 0,
      60,
    );

    console.log(`✨ 檢測結果: ${specialMoves.length > 0 ? specialMoves.join(', ') : '無特殊招式'}`);

    if (specialMoves.length > 0) {
      specialMoves.forEach((moveType, index) => {
        setTimeout(() => {
          const title = t(`special.${moveType}`);

          // 🎆 顯示視覺化特殊招式UI
          this.showSpecialMoveUI(title, moveType);

          // 顯示舊版訊息作為備用
          this.showMessage(`⚡ ${title}`);
          console.log(`⚡ 特殊招式: ${title}`);
        }, index * 1000);
      });
    }
  }

  /**
   * 顯示視覺化特殊招式UI
   */
  private showSpecialMoveUI(title: string, moveType: string): void {
    if (!this.specialMoveDisplay) {
      console.warn('⚡ UI: specialMoveDisplay 元素不存在');
      return;
    }

    console.log(`⚡ UI: 顯示特殊招式UI - ${title} (${moveType})`);

    const titleElement = this.specialMoveDisplay.querySelector('.special-move-title') as HTMLElement;
    const subtitleElement = this.specialMoveDisplay.querySelector('.special-move-subtitle') as HTMLElement;

    if (titleElement) {
      titleElement.textContent = title;
      console.log(`⚡ UI: 設置標題文字 - ${title}`);
    } else {
      console.warn('⚡ UI: 找不到 .special-move-title 元素');
    }

    if (subtitleElement) {
      const subtitle = t(`special.${moveType}.subtitle`);
      subtitleElement.textContent = subtitle;
      console.log(`⚡ UI: 設置副標題文字 - ${subtitle}`);
    } else {
      console.warn('⚡ UI: 找不到 .special-move-subtitle 元素');
    }

    // 根據招式類型設定不同顏色
    const effectColors = {
      corner_master: '#ffd700',
      mega_flip: '#ff4500',
      phoenix_rise: '#ff69b4',
      domination: '#9932cc',
      time_warp: '#8b00ff',
    };

    const color = effectColors[moveType as keyof typeof effectColors] || '#ffd700';
    if (titleElement) {
      // 直接設置文字顏色而非背景
      titleElement.style.color = color;
      titleElement.style.textShadow = `
        0 0 20px ${color}88,
        0 0 40px ${color}66,
        0 4px 8px rgba(0, 0, 0, 0.8)
      `;
      console.log(`⚡ UI: 設置顏色 - ${color}`);
    }

    // 顯示動畫
    this.specialMoveDisplay.classList.remove('hidden');
    this.specialMoveDisplay.classList.add('special-move-animation');
    console.log('⚡ UI: 顯示動畫已啟動');

    // 2.5秒後隱藏
    setTimeout(() => {
      this.specialMoveDisplay?.classList.add('hidden');
      this.specialMoveDisplay?.classList.remove('special-move-animation');
    }, 2500);
  }

  private refreshUI(): void {
    switch (this.currentScreen) {
      case 'menu':
        this.showMenu();
        break;
      case 'settings':
        this.showSettings();
        break;
      case 'help':
        this.showHelp();
        break;
      case 'game':
        this.showGameScreen();
        this.updateDisplay();
        break;
    }
  }

  // ===== MENU SCREENS =====

  private showMenu(): void {
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer) {return;}

    // Clean up any ongoing game state and AI processes
    this.cleanupGameState();

    this.currentScreen = 'menu';
    const header = document.getElementById('game-header');
    const headerBackBtn = document.getElementById('header-back-btn');
    const sharedHealthBarContainer = document.getElementById('shared-health-bar-container');

    this.showElement(header);
    this.hideElement(headerBackBtn);
    this.hideElement(sharedHealthBarContainer);

    // 清理遊戲狀態顯示
    const blackScore = document.getElementById('black-score');
    const whiteScore = document.getElementById('white-score');
    const currentTurn = document.getElementById('current-turn');
    if (blackScore) {blackScore.textContent = '';}
    if (whiteScore) {whiteScore.textContent = '';}
    if (currentTurn) {currentTurn.textContent = '';}

    // 重置遊戲狀態
    this.gameState = null;

    screenContainer.innerHTML = `
      <div class="menu-screen">
        <h2 class="menu-title">${t('menu.chooseMode')}</h2>
        <button class="menu-button" id="pvp-btn">👥 ${t('menu.pvp')}</button>
        <button class="menu-button" id="pvc-btn">🤖 ${t('menu.pvc')}</button>
        <button class="menu-button" id="menu-settings-btn">⚙️ ${t('menu.settings')}</button>
        <button class="menu-button" id="help-btn">❓ ${t('menu.help')}</button>
      </div>
    `;

    document.getElementById('pvp-btn')?.addEventListener('click', () => {
      this.startNewGame('pvp');
    });

    document.getElementById('pvc-btn')?.addEventListener('click', () => {
      this.showAIDifficultyMenu();
    });

    document.getElementById('menu-settings-btn')?.addEventListener('click', () => {
      this.showSettings();
    });

    document.getElementById('help-btn')?.addEventListener('click', () => {
      this.showHelp();
    });
  }

  private showAIDifficultyMenu(): void {
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer) {return;}

    this.currentScreen = 'difficulty';
    const headerBackBtn = document.getElementById('header-back-btn');
    this.showElement(headerBackBtn);

    screenContainer.innerHTML = `
      <div class="ai-selection-screen">
        <h2 class="menu-title">${t('menu.selectDifficulty')}</h2>

        <!-- AI Level Tabs -->
        <div class="ai-level-tabs">
          <button class="ai-level-tab active" data-level="beginner">
            <span class="level-icon">🌱</span>
            <span class="level-name">${t('ai.level.beginner')}</span>
          </button>
          <button class="ai-level-tab" data-level="intermediate">
            <span class="level-icon">📚</span>
            <span class="level-name">${t('ai.level.intermediate')}</span>
          </button>
          <button class="ai-level-tab" data-level="advanced">
            <span class="level-icon">⚔️</span>
            <span class="level-name">${t('ai.level.advanced')}</span>
          </button>
          <button class="ai-level-tab" data-level="professional">
            <span class="level-icon">🏆</span>
            <span class="level-name">${t('ai.level.professional')}</span>
          </button>
        </div>

        <!-- AI Options Grid -->
        <div class="ai-options-grid" id="ai-options-grid">
          <!-- Dynamic AI options will be loaded here -->
        </div>

        <!-- AI Info Box -->
        <div class="ai-info-box" id="ai-info-box">
          <div class="ai-info-header">
            <h3 id="ai-info-name">${t('ai.info.namePlaceholder')}</h3>
            <div class="ai-strength" id="ai-strength">${this.getStrengthStars(1)}</div>
          </div>
          <p class="ai-description" id="ai-description">${t('ai.info.descriptionPlaceholder')}</p>
          <div class="ai-details">
            <div class="ai-detail">
              <span class="detail-label">${t('ai.info.thinkingTime')}</span>
              <span class="detail-value" id="ai-thinking-time">${t('ai.info.thinkingTimePlaceholder')}</span>
            </div>
            <div class="ai-detail">
              <span class="detail-label">${t('ai.info.searchDepth')}</span>
              <span class="detail-value" id="ai-search-depth">${t('ai.info.searchDepthPlaceholder')}</span>
            </div>
            <div class="ai-features" id="ai-features">
              <span class="feature">${t('ai.info.featurePlaceholder')}</span>
            </div>
          </div>
        </div>

        <!-- Custom Settings Panel (hidden by default) -->
        <div class="ai-custom-settings is-hidden" id="ai-custom-settings">
          <h4>${t('ai.custom.title')}</h4>
          <div class="custom-setting">
            <label>${t('ai.custom.thinkingTime')}</label>
            <input type="range" id="custom-thinking-time" min="1" max="30" value="5" step="0.5">
            <span id="thinking-time-value">5.0</span>
          </div>
          <div class="custom-setting">
            <label>${t('ai.custom.searchDepth')}</label>
            <input type="range" id="custom-search-depth" min="1" max="12" value="6">
            <span id="search-depth-value">6</span>
          </div>
          <div class="custom-setting">
            <label>
              <input type="checkbox" id="custom-opening-book" checked>
              ${t('ai.custom.useOpeningBook')}
            </label>
          </div>
          <div class="custom-setting">
            <label>
              <input type="checkbox" id="custom-endgame-solver" checked>
              ${t('ai.custom.useEndgameSolver')}
            </label>
          </div>
          <div class="custom-setting">
            <label>${t('ai.custom.personality')}</label>
            <select id="custom-personality">
              <option value="balanced">${t('ai.custom.personality.balanced')}</option>
              <option value="aggressive">${t('ai.custom.personality.aggressive')}</option>
              <option value="defensive">${t('ai.custom.personality.defensive')}</option>
              <option value="positional">${t('ai.custom.personality.positional')}</option>
            </select>
          </div>
        </div>

        <!-- Start Game Button -->
        <button class="menu-button" id="start-ai-btn">${t('menu.startGame')}</button>
      </div>
    `;

    // Initialize AI selection system
    this.initializeAISelection();
  }

  private initializeAISelection(): void {
    // Import AI configuration types
    import('./types/ai-config-types').then(({ AI_OPTIONS_DATABASE, getDefaultAIForLevel }) => {
      this.setupAISelectionHandlers(AI_OPTIONS_DATABASE, getDefaultAIForLevel);
    });
  }

  private setupAISelectionHandlers(aiDatabase: any, getDefaultAIForLevel: any): void {
    let currentLevel: string = 'beginner';

    // Tab switching logic
    const levelTabs = document.querySelectorAll('.ai-level-tab');
    levelTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        // Update active tab
        levelTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        // Get selected level
        currentLevel = tab.getAttribute('data-level') || 'beginner';

        // Load AI options for this level
        this.loadAIOptions(currentLevel, aiDatabase);

        // Select default AI for this level
        const defaultAI = getDefaultAIForLevel(currentLevel);
        if (defaultAI) {
          this.selectedAI = defaultAI.id;
          this.updateAIInfo(defaultAI);
        }
      });
    });

    // Initialize with beginner level
    this.loadAIOptions('beginner', aiDatabase);
    const defaultAI = getDefaultAIForLevel('beginner');
    if (defaultAI) {
      this.selectedAI = defaultAI.id;
      this.updateAIInfo(defaultAI);
    }

    // Start game button
    document.getElementById('start-ai-btn')?.addEventListener('click', () => {
      if (this.selectedAI) {
        // If custom AI, apply custom settings
        if (this.selectedAI === AIDifficulty.CUSTOM && this.customSettings) {
          defaultAIManager.updateCustomAIConfig(this.customSettings);
        }
        this.startNewGame('pvc', this.selectedAI);
      }
    });

    // Custom settings panel
    this.initializeCustomSettings();
  }

  private loadAIOptions(level: string, aiDatabase: any): void {
    const optionsGrid = document.getElementById('ai-options-grid');
    if (!optionsGrid) return;

    const aiOptions = aiDatabase[level] || [];

    optionsGrid.innerHTML = aiOptions.map((option: any) => `
      <div class="ai-option-card" data-ai-id="${option.id}">
        <div class="ai-card-header">
          <h4>${t(option.nameKey)}</h4>
          <div class="ai-strength">${this.getStrengthStars(option.strength)}</div>
        </div>
        <p class="ai-card-description">${t(option.descriptionKey)}</p>
        <div class="ai-card-features">
          ${option.featureKeys.map((featureKey: string) => `<span class="feature">${t(featureKey)}</span>`).join('')}
        </div>
      </div>
    `).join('');

    // Add click handlers for AI options
    const optionCards = optionsGrid.querySelectorAll('.ai-option-card');
    optionCards.forEach(card => {
      card.addEventListener('click', () => {
        // Update selected card
        optionCards.forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');

        // Get AI ID
        const aiId = card.getAttribute('data-ai-id');
        const aiOption = aiOptions.find((opt: any) => opt.id === aiId);

        if (aiOption) {
          this.selectedAI = aiOption.id;
          this.updateAIInfo(aiOption);

          // Show/hide custom settings panel
          this.toggleCustomSettings(aiOption.isCustomizable);
        }
      });
    });

    // Auto-select first option
    if (optionCards.length > 0) {
      optionCards[0]!.classList.add('selected');
    }
  }

  private updateAIInfo(aiOption: any): void {
    const nameEl = document.getElementById('ai-info-name');
    const strengthEl = document.getElementById('ai-strength');
    const descEl = document.getElementById('ai-description');
    const thinkingTimeEl = document.getElementById('ai-thinking-time');
    const searchDepthEl = document.getElementById('ai-search-depth');
    const featuresEl = document.getElementById('ai-features');

    if (nameEl) nameEl.textContent = t(aiOption.nameKey);
    if (strengthEl) strengthEl.textContent = this.getStrengthStars(aiOption.strength);
    if (descEl) descEl.textContent = t(aiOption.descriptionKey);
    if (thinkingTimeEl) thinkingTimeEl.textContent = t(aiOption.thinkingTimeKey);
    if (searchDepthEl) searchDepthEl.textContent = t(aiOption.searchDepthKey);

    if (featuresEl) {
      featuresEl.innerHTML = aiOption.featureKeys
        .map((featureKey: string) => `<span class="feature">${t(featureKey)}</span>`)
        .join('');
    }
  }

  private getStrengthStars(strength: number): string {
    return '★'.repeat(strength) + '☆'.repeat(5 - strength);
  }

  private toggleCustomSettings(show: boolean): void {
    const customPanel = document.getElementById('ai-custom-settings');
    if (customPanel) {
      customPanel.classList.toggle('is-hidden', !show);
    }
  }

  private initializeCustomSettings(): void {
    const thinkingTimeSlider = document.getElementById('custom-thinking-time') as HTMLInputElement;
    const thinkingTimeValue = document.getElementById('thinking-time-value');
    const searchDepthSlider = document.getElementById('custom-search-depth') as HTMLInputElement;
    const searchDepthValue = document.getElementById('search-depth-value');

    // Thinking time slider
    if (thinkingTimeSlider && thinkingTimeValue) {
      thinkingTimeSlider.addEventListener('input', () => {
        thinkingTimeValue.textContent = parseFloat(thinkingTimeSlider.value).toFixed(1);
      });
    }

    // Search depth slider
    if (searchDepthSlider && searchDepthValue) {
      searchDepthSlider.addEventListener('input', () => {
        searchDepthValue.textContent = searchDepthSlider.value;
      });
    }

    // Apply custom settings when values change
    const customInputs = document.querySelectorAll('#ai-custom-settings input, #ai-custom-settings select');
    customInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateCustomSettings();
      });
    });
  }

  private updateCustomSettings(): void {
    const thinkingTime = parseFloat((document.getElementById('custom-thinking-time') as HTMLInputElement)?.value || '5');
    const searchDepth = parseInt((document.getElementById('custom-search-depth') as HTMLInputElement)?.value || '6');
    const useOpeningBook = (document.getElementById('custom-opening-book') as HTMLInputElement)?.checked || false;
    const useEndgameSolver = (document.getElementById('custom-endgame-solver') as HTMLInputElement)?.checked || false;
    const personality = (document.getElementById('custom-personality') as HTMLSelectElement)?.value || 'balanced';

    this.customSettings = {
      thinkingTime,
      searchDepth,
      useOpeningBook,
      useEndgameSolver,
      adaptiveTime: true,
      personality,
      iterativeDeepening: true,
      useTranspositionTable: true,
    };
  }

  private showSettings(): void {
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer) {return;}

    this.currentScreen = 'settings';
    const headerBackBtn = document.getElementById('header-back-btn');
    this.showElement(headerBackBtn);

    screenContainer.innerHTML = `
      <div class="settings-screen">
        <h2 class="settings-title">${t('settings.title')}</h2>

        <div class="settings-group">
          <h3>${t('settings.language')}</h3>
          <select id="language-select" class="language-select">
            <option value="en">English</option>
            <option value="zh-Hant">繁體中文</option>
            <option value="zh-Hans">简体中文</option>
          </select>
        </div>

        <div class="settings-group">
          <h3>${t('settings.visual')}</h3>
          <label>
            <input type="checkbox" id="show-valid" checked>
            <span>${t('settings.showValid')}</span>
          </label>
          <label>
            <input type="checkbox" id="show-last" checked>
            <span>${t('settings.showLast')}</span>
          </label>
          <label>
            <input type="checkbox" id="show-mobility">
            <span>${t('settings.showMobility')}</span>
          </label>
          <label>
            <input type="checkbox" id="animations" checked>
            <span>${t('settings.animations')}</span>
          </label>
        </div>

        <div class="settings-group">
          <h3>${t('settings.game')}</h3>
          <label>
            <input type="checkbox" id="auto-pass" checked>
            <span>${t('settings.autoPass')}</span>
          </label>
          <label>
            <input type="checkbox" id="confirm-moves">
            <span>${t('settings.confirm')}</span>
          </label>
        </div>

        <button class="menu-button menu-button--spaced" id="save-settings">
          ${t('settings.save')}
        </button>
      </div>
    `;

    const settings = this.getSettings();
    (document.getElementById('language-select') as HTMLSelectElement).value = settings.language || 'en';
    (document.getElementById('show-valid') as HTMLInputElement).checked = settings.showValid;
    (document.getElementById('show-last') as HTMLInputElement).checked = settings.showLast;
    (document.getElementById('show-mobility') as HTMLInputElement).checked = settings.showMobility;
    (document.getElementById('animations') as HTMLInputElement).checked = settings.animations;
    (document.getElementById('auto-pass') as HTMLInputElement).checked = settings.autoPass;
    (document.getElementById('confirm-moves') as HTMLInputElement).checked = settings.confirmMoves;

    document.getElementById('save-settings')?.addEventListener('click', () => {
      const lang = (document.getElementById('language-select') as HTMLSelectElement).value as Language;
      const settings = {
        language: lang,
        showValid: (document.getElementById('show-valid') as HTMLInputElement)?.checked,
        showLast: (document.getElementById('show-last') as HTMLInputElement)?.checked,
        showMobility: (document.getElementById('show-mobility') as HTMLInputElement)?.checked,
        animations: (document.getElementById('animations') as HTMLInputElement)?.checked,
        autoPass: (document.getElementById('auto-pass') as HTMLInputElement)?.checked,
        confirmMoves: (document.getElementById('confirm-moves') as HTMLInputElement)?.checked,
      };
      localStorage.setItem('reversi-settings', JSON.stringify(settings));
      localStorage.setItem('language', lang);
      setLanguage(lang);
      applyTranslations();

      // Update particle system enabled state when animations setting changes
      this.particleSystem.setEnabled(settings.animations);

      if (this.modalService) {
        this.modalService.showSuccess(
          t('settings.saved'),
          t('modal.settingsSaved.title'),
        );
      }
      this.refreshUI();
    });
  }

  private showHelp(): void {
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer) {return;}

    this.currentScreen = 'help';
    const headerBackBtn = document.getElementById('header-back-btn');
    this.showElement(headerBackBtn);

    screenContainer.innerHTML = `
      <div class="help-screen">
        <h2>${t('help.title')}</h2>

        <h3>${t('help.objectiveTitle')}</h3>
        <p>${t('help.objectiveText')}</p>

        <h3>${t('help.rulesTitle')}</h3>
        <ul>
          <li>${t('help.rules1')}</li>
          <li>${t('help.rules2')}</li>
          <li>${t('help.rules3')}</li>
          <li>${t('help.rules4')}</li>
          <li>${t('help.rules5')}</li>
          <li>${t('help.rules6')}</li>
        </ul>

        <h3>${t('help.strategyTitle')}</h3>
        <ul>
          <li>${t('help.strategy1')}</li>
          <li>${t('help.strategy2')}</li>
          <li>${t('help.strategy3')}</li>
          <li>${t('help.strategy4')}</li>
        </ul>

        <h3>${t('help.controlsTitle')}</h3>
        <ul>
          <li>${t('help.controls1')}</li>
          <li>${t('help.controls2')}</li>
          <li>${t('help.controls3')}</li>
        </ul>
      </div>
    `;
  }

  // ===== GAME LOGIC =====

  private startNewGame(mode: 'pvp' | 'pvc', aiDifficulty: AIDifficulty = AIDifficulty.GREEDY): void {
    // Initialize game state
    this.gameState = {
      board: this.createInitialBoard(),
      currentPlayer: BLACK,
      blackScore: 2,
      whiteScore: 2,
      gameMode: mode,
      isGameOver: false,
      validMoves: new Set(),
      moveHistory: [],
      aiDifficulty: aiDifficulty,
    };

    // Calculate initial valid moves
    this.updateValidMoves();

    // Show game screen
    this.showGameScreen();

    // Update display
    this.updateDisplay();
  }

  private createInitialBoard(): number[] {
    const board = new Array(64).fill(EMPTY);
    board[27] = WHITE;
    board[28] = BLACK;
    board[35] = BLACK;
    board[36] = WHITE;
    return board;
  }

  private showGameScreen(): void {
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer || !this.gameState) {return;}

    this.currentScreen = 'game';
    const header = document.getElementById('game-header');
    const headerBackBtn = document.getElementById('header-back-btn');
    const sharedHealthBarContainer = document.getElementById('shared-health-bar-container');

    this.showElement(header);
    this.showElement(headerBackBtn);
    this.showElement(sharedHealthBarContainer);

    const modeText = this.gameState.gameMode === 'pvp'
      ? t('game.pvp')
      : `${t('game.pvai')} (${t('difficulty.' + this.gameState.aiDifficulty)})`;

    screenContainer.innerHTML = `
      <div class="game-screen">
        <div class="game-info-compact">
          <div>${t('game.mode')}: ${modeText}</div>
          <div id="ai-status"></div>
        </div>
        <div class="game-board" id="game-board"></div>
        <div id="game-over-modal" class="game-over-modal is-hidden"></div>
      </div>
    `;

    // Create board cells
    this.createBoardCells();
  }

  private createBoardCells(): void {
    const board = document.getElementById('game-board');
    if (!board || !this.gameState) {return;}

    board.innerHTML = '';

    for (let i = 0; i < 64; i++) {
      const cell = document.createElement('div');
      cell.className = 'board-cell';
      cell.dataset.position = i.toString();

      // Add click handler
      cell.addEventListener('click', () => {
        if (!this.isProcessingMove && !this.aiThinking) {
          this.handleCellClick(i);
        }
      });

      board.appendChild(cell);
    }

    // Render initial pieces
    this.renderBoard();
  }

  private renderBoard(): void {
    if (!this.gameState) {return;}

    const settings = this.getSettings();

    // Calculate mobility for all valid moves if setting is enabled
    const mobilityMap = new Map<number, { player: number; opponent: number }>();
    if (settings.showMobility && this.gameState.validMoves.size > 0) {
      for (const position of this.gameState.validMoves) {
        const mobility = this.calculateMoveMobility(position, this.gameState.currentPlayer);
        mobilityMap.set(position, mobility);
      }
    }

    for (let i = 0; i < 64; i++) {
      const cell = document.querySelector(`[data-position="${i}"]`) as HTMLElement;
      if (!cell) {continue;}

      // Clear cell classes
      cell.className = 'board-cell';

      // Clear cell content first
      cell.innerHTML = '';

      // Add piece if exists
      if (this.gameState.board[i] !== EMPTY) {
        const piece = document.createElement('div');
        piece.className = `piece ${this.gameState.board[i] === BLACK ? 'piece-black' : 'piece-white'}`;
        cell.appendChild(piece);
      }

      // Show valid moves
      if (settings.showValid && this.gameState.validMoves.has(i)) {
        cell.classList.add('valid-move');

        // Show mobility if enabled
        if (settings.showMobility && mobilityMap.has(i)) {
          const mobility = mobilityMap.get(i)!;
          const mobilityIndicator = document.createElement('div');
          mobilityIndicator.className = 'mobility-indicator';

          // Display as subtraction result and color code based on mobility difference
          const difference = mobility.player - mobility.opponent;
          mobilityIndicator.textContent = difference > 0 ? `+${difference}` : `${difference}`;
          if (difference > 0) {
            mobilityIndicator.classList.add('mobility-positive');
          } else if (difference < 0) {
            mobilityIndicator.classList.add('mobility-negative');
          } else {
            mobilityIndicator.classList.add('mobility-neutral');
          }

          cell.appendChild(mobilityIndicator);
        }
      }

      // Show last move
      if (settings.showLast && this.gameState.moveHistory.length > 0) {
        const lastMove = this.gameState.moveHistory[this.gameState.moveHistory.length - 1];
        if (lastMove && lastMove.position === i) {
          cell.classList.add('last-move');
        }
      }
    }
  }

  private getSettings(): any {
    const stored = localStorage.getItem('reversi-settings');
    if (stored) {
      return JSON.parse(stored);
    }
    const lang = (localStorage.getItem('language') as Language) || 'en';
    return {
      language: lang,
      showValid: true,
      showLast: true,
      showMobility: false,
      animations: true,
      autoPass: true,
      confirmMoves: false,
    };
  }

  private async handleCellClick(position: number): Promise<void> {
    if (!this.gameState || this.gameState.isGameOver) {return;}

    // Check if it's a valid move
    if (!this.gameState.validMoves.has(position)) {
      console.log(t('messages.invalidMove'));
      return;
    }

    // For PvC mode with confirmMoves setting
    const settings = this.getSettings();
    if (this.gameState.gameMode === 'pvc' &&
        this.gameState.currentPlayer === BLACK &&
        settings.confirmMoves) {
      const confirmed = await this.showSimpleConfirm(
        t('messages.moveConfirm'),
        t('modal.moveConfirm.title'),
      );
      if (!confirmed) {
        return;
      }
    }

    // Make the move
    this.makeMove(position);
  }

  private makeMove(position: number): void {
    if (!this.gameState) {return;}

    this.isProcessingMove = true;

    // Get settings for animation checks
    const settings = this.getSettings();

    // Get pieces to flip
    const toFlip = this.getFlippedPieces(position, this.gameState.currentPlayer);

    // Analyze game situation for special effects
    const specialEffects: SpecialEffect = {
      corner: position === 0 || position === 7 || position === 56 || position === 63,
      edge: this.isEdgePosition(position),
      massive: toFlip.length >= 10,
      chain: this.isChainMove(toFlip),
      comeback: this.isComebackMove(),
      domination: this.isDominationMove(),
    };

    // Calculate move grade with special effects
    const grade = this.particleSystem.calculateGrade(toFlip.length, position, specialEffects);

    // Get cell position for particle effects
    const cell = document.querySelector(`[data-position="${position}"]`) as HTMLElement;
    if (cell) {
      const rect = cell.getBoundingClientRect();
      const containerRect = this.container!.getBoundingClientRect();
      const x = rect.left + rect.width / 2 - containerRect.left;
      const y = rect.top + rect.height / 2 - containerRect.top;

      // Calculate flipped positions in pixel coordinates for directional particles
      const flippedPixelPositions = toFlip.map(pos => {
        const flippedCell = document.querySelector(`[data-position="${pos}"]`) as HTMLElement;
        if (flippedCell) {
          const flippedRect = flippedCell.getBoundingClientRect();
          return {
            x: flippedRect.left + flippedRect.width / 2 - containerRect.left,
            y: flippedRect.top + flippedRect.height / 2 - containerRect.top,
          };
        }
        return { x, y }; // Fallback to source position
      });

      // Trigger particle effect based on grade with special effects and directional info
      if (settings.animations) {
        this.particleSystem.triggerMoveEffect(x, y, grade, toFlip.length, specialEffects, flippedPixelPositions);

        // Additional special effects
        if (specialEffects.chain) {
          this.particleSystem.createChainEffect(x, y);
        }

        // Show grade indicator
        this.showGradeIndicator(grade, x, y);
      }
    }

    // Record move in history
    this.gameState.moveHistory.push({
      position: position,
      player: this.gameState.currentPlayer,
      flipped: toFlip,
    });

    // Add screen shake for higher grades
    this.triggerScreenShake(grade);

    // Place the piece with animation
    this.gameState.board[position] = this.gameState.currentPlayer;
    if (cell) {
      const piece = document.createElement('div');
      piece.className = `piece ${this.gameState.currentPlayer === BLACK ? 'piece-black' : 'piece-white'} placing`;
      cell.innerHTML = '';
      cell.appendChild(piece);
    }

    // Flip pieces with animation and particles
    if (settings.animations) {
      setTimeout(() => {
        this.flipPiecesWithEffects(toFlip, grade);
        // Adjust timeout based on number of pieces being flipped
        const flipDuration = toFlip.length * 80 + 300;
        setTimeout(() => {
          this.afterMove();
        }, flipDuration);
      }, 200);
    } else {
      this.flipPiecesWithEffects(toFlip, grade);
      this.afterMove();
    }
  }

  private showGradeIndicator(grade: Grade, x: number, y: number): void {
    // Create a temporary grade indicator
    const indicator = document.createElement('div');
    indicator.style.position = 'absolute';
    indicator.style.left = `${x}px`;
    indicator.style.top = `${y - 50}px`;
    indicator.style.transform = 'translateX(-50%)';
    indicator.style.fontSize = '3rem';
    indicator.style.fontWeight = 'bold';
    indicator.style.zIndex = '200';
    indicator.style.pointerEvents = 'none';
    indicator.style.animation = 'gradePopup 1s ease-out forwards';

    // Set grade text and color
    switch (grade) {
      case 'SSS':
        indicator.textContent = 'SSS';
        indicator.style.color = '#ff0080';
        indicator.style.textShadow = '0 0 1.875rem #ff0080, 0 0 3.75rem #ff69b4';
        indicator.style.fontSize = '3.5rem';
        indicator.style.animation = 'gradePopup 2s ease-out forwards, rainbow 2s infinite';
        break;
      case 'SS':
        indicator.textContent = 'SS';
        indicator.style.color = '#9932cc';
        indicator.style.textShadow = '0 0 1.5625rem #9932cc, 0 0 3.125rem #8b00ff';
        indicator.style.fontSize = '3.25rem';
        indicator.style.animation = 'gradePopup 1.5s ease-out forwards, glow 1.5s infinite';
        break;
      case 'S':
        indicator.textContent = 'S';
        indicator.style.color = '#ff69b4';
        indicator.style.textShadow = '0 0 1.25rem #ff69b4';
        indicator.style.fontSize = '3rem';
        break;
      case 'A':
        indicator.textContent = 'A';
        indicator.style.color = '#ffd700';
        indicator.style.textShadow = '0 0 1.25rem #ffd700';
        break;
      case 'B':
        indicator.textContent = 'B';
        indicator.style.color = '#00ff7f';
        indicator.style.textShadow = '0 0 0.9375rem #00ff7f';
        break;
      case 'C':
        indicator.textContent = 'C';
        indicator.style.color = '#888888';
        indicator.style.textShadow = '0 0 0.625rem #888888';
        break;
    }

    this.container?.appendChild(indicator);

    // Remove after animation
    setTimeout(() => {
      indicator.remove();
    }, 1000);
  }

  private flipPiecesWithEffects(positions: number[], grade?: Grade): void {
    if (!this.gameState) {return;}

    const settings = this.getSettings();

    if (settings.animations) {
      // Sequential flipping with delays for dynamic effect
      positions.forEach((pos, index) => {
        setTimeout(() => {
          this.gameState!.board[pos] = this.gameState!.currentPlayer;

          const cell = document.querySelector(`[data-position="${pos}"]`) as HTMLElement;
          if (cell) {
            // Create flip particle effect with grade-based colors
            const rect = cell.getBoundingClientRect();
            const containerRect = this.container!.getBoundingClientRect();
            const x = rect.left + rect.width / 2 - containerRect.left;
            const y = rect.top + rect.height / 2 - containerRect.top;

            const color = this.gameState!.currentPlayer === BLACK ? '#000000' : '#ffffff';
            this.particleSystem.createFlipEffect(x, y, color, grade);

            const piece = cell.querySelector('.piece');
            if (piece) {
              piece.classList.add('flipping');
              setTimeout(() => {
                piece.className = `piece ${this.gameState!.currentPlayer === BLACK ? 'piece-black' : 'piece-white'}`;
              }, 250);
            }
          }
        }, index * 80); // 80ms delay between each flip for dynamic effect
      });
    } else {
      // Immediate flipping when animations are disabled
      positions.forEach(pos => {
        this.gameState!.board[pos] = this.gameState!.currentPlayer;

        const cell = document.querySelector(`[data-position="${pos}"]`) as HTMLElement;
        if (cell) {
          const piece = cell.querySelector('.piece');
          if (piece) {
            piece.className = `piece ${this.gameState!.currentPlayer === BLACK ? 'piece-black' : 'piece-white'}`;
          }
        }
      });
    }
  }

  private flipPieces(positions: number[]): void {
    if (!this.gameState) {return;}

    const settings = this.getSettings();

    positions.forEach(pos => {
      this.gameState!.board[pos] = this.gameState!.currentPlayer;

      const cell = document.querySelector(`[data-position="${pos}"]`) as HTMLElement;
      if (cell) {
        const piece = cell.querySelector('.piece');
        if (piece && settings.animations) {
          piece.classList.add('flipping');
          setTimeout(() => {
            piece.className = `piece ${this.gameState!.currentPlayer === BLACK ? 'piece-black' : 'piece-white'}`;
          }, 250);
        } else if (piece) {
          piece.className = `piece ${this.gameState!.currentPlayer === BLACK ? 'piece-black' : 'piece-white'}`;
        }
      }
    });
  }

  private afterMove(): void {
    if (!this.gameState) {return;}

    // Update scores
    this.updateScores();

    // Switch player
    this.gameState.currentPlayer = this.gameState.currentPlayer === BLACK ? WHITE : BLACK;

    // Update valid moves for new player
    this.updateValidMoves();

    // 🔥 戰鬥系統增強：檢測連擊和特殊招式
    const previousPlayer = this.gameState.currentPlayer === BLACK ? WHITE : BLACK;
    const opponentCanMove = this.gameState.validMoves.size > 0;
    const lastMove = this.gameState.moveHistory[this.gameState.moveHistory.length - 1];

    if (lastMove) {
      // 處理連擊 (當對手無法移動時)
      this.comboSystem.processMove(
        previousPlayer === BLACK ? 1 : 2, // 轉換為 PlayerColor enum 值
        opponentCanMove,
      );

      // 檢測特殊招式
      this.detectSpecialMoves(
        lastMove.position,
        lastMove.flipped, // 傳遞翻轉位置陣列而非長度
        previousPlayer === BLACK ? this.gameState.blackScore : this.gameState.whiteScore,
        previousPlayer === BLACK ? this.gameState.whiteScore : this.gameState.blackScore,
      );
    }

    // Check for game over or pass
    const gameContinues = !this.checkGameStatus();

    if (gameContinues) {
      // Update display
      this.updateDisplay();
    }

    this.isProcessingMove = false;

    // If game continues and it's AI's turn, trigger AI move after clearing processing flag
    if (gameContinues &&
        this.gameState.gameMode === 'pvc' &&
        this.gameState.currentPlayer === WHITE) {
      this.makeAIMove();
    }
  }

  private updateValidMoves(): void {
    if (!this.gameState) {return;}

    this.gameState.validMoves.clear();

    for (let i = 0; i < 64; i++) {
      if (this.gameState.board[i] === EMPTY) {
        if (this.isValidMove(i, this.gameState.currentPlayer)) {
          this.gameState.validMoves.add(i);
        }
      }
    }
  }

  private isValidMove(position: number, player: number): boolean {
    if (!this.gameState || this.gameState.board[position] !== EMPTY) {
      return false;
    }

    // Check all directions
    for (const [dx, dy] of DIRECTIONS) {
      if (this.checkDirection(position, player, dx || 0, dy || 0).length > 0) {
        return true;
      }
    }

    return false;
  }

  private checkDirection(position: number, player: number, dx: number, dy: number): number[] {
    if (!this.gameState) {return [];}

    const flipped: number[] = [];
    const row = Math.floor(position / 8);
    const col = position % 8;
    const opponent = player === BLACK ? WHITE : BLACK;

    let r = row + dy;
    let c = col + dx;

    // Find opponent pieces
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const pos = r * 8 + c;

      if (this.gameState.board[pos] === EMPTY) {
        return []; // Empty cell, no valid line
      } else if (this.gameState.board[pos] === opponent) {
        flipped.push(pos);
      } else {
        // Found our piece
        return flipped.length > 0 ? flipped : [];
      }

      r += dy;
      c += dx;
    }

    return []; // Hit edge without finding our piece
  }

  private getFlippedPieces(position: number, player: number): number[] {
    const allFlipped: number[] = [];

    for (const [dx, dy] of DIRECTIONS) {
      const flipped = this.checkDirection(position, player, dx, dy);
      allFlipped.push(...flipped);
    }

    return allFlipped;
  }

  private calculateMoveMobility(position: number, player: number): { player: number; opponent: number } {
    if (!this.gameState) {return { player: 0, opponent: 0 };}

    // Create a temporary board with the move applied
    const tempBoard = [...this.gameState.board];
    const flippedPieces = this.getFlippedPieces(position, player);

    // Apply the move
    tempBoard[position] = player;
    for (const pos of flippedPieces) {
      tempBoard[pos] = player;
    }

    // Calculate valid moves for both players after this move
    const opponent = player === BLACK ? WHITE : BLACK;

    // Count opponent's valid moves after this move
    let opponentMoves = 0;
    for (let i = 0; i < 64; i++) {
      if (tempBoard[i] === EMPTY) {
        if (this.isValidMoveOnBoard(i, opponent, tempBoard)) {
          opponentMoves++;
        }
      }
    }

    // Count player's valid moves after this move (after opponent's turn)
    let playerMoves = 0;
    for (let i = 0; i < 64; i++) {
      if (tempBoard[i] === EMPTY) {
        if (this.isValidMoveOnBoard(i, player, tempBoard)) {
          playerMoves++;
        }
      }
    }

    // Return both player and opponent mobility
    return { player: playerMoves, opponent: opponentMoves };
  }

  private isValidMoveOnBoard(position: number, player: number, board: number[]): boolean {
    if (board[position] !== EMPTY) {
      return false;
    }

    // Check all directions
    for (const [dx, dy] of DIRECTIONS) {
      if (this.checkDirectionOnBoard(position, player, dx || 0, dy || 0, board).length > 0) {
        return true;
      }
    }

    return false;
  }

  private checkDirectionOnBoard(position: number, player: number, dx: number, dy: number, board: number[]): number[] {
    const flipped: number[] = [];
    const row = Math.floor(position / 8);
    const col = position % 8;
    const opponent = player === BLACK ? WHITE : BLACK;

    let r = row + dy;
    let c = col + dx;

    // Find opponent pieces
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const pos = r * 8 + c;

      if (board[pos] === EMPTY) {
        return []; // Empty cell, no valid line
      } else if (board[pos] === opponent) {
        flipped.push(pos);
      } else {
        // Found our piece
        return flipped.length > 0 ? flipped : [];
      }

      r += dy;
      c += dx;
    }

    return []; // Hit edge without finding our piece
  }

  private updateScores(): void {
    if (!this.gameState) {return;}

    // Store old scores for animation
    const oldBlackScore = this.gameState.blackScore;
    const oldWhiteScore = this.gameState.whiteScore;

    let black = 0;
    let white = 0;

    for (const piece of this.gameState.board) {
      if (piece === BLACK) {black++;}
      else if (piece === WHITE) {white++;}
    }

    this.gameState.blackScore = black;
    this.gameState.whiteScore = white;

    // Trigger shared health bar animations if scores changed
    if (oldBlackScore !== black) {
      this.animateSharedHealthChange('black', oldBlackScore, black);
    }
    if (oldWhiteScore !== white) {
      this.animateSharedHealthChange('white', oldWhiteScore, white);
    }
  }

  private checkGameStatus(): boolean {
    if (!this.gameState) {return false;}

    // No valid moves for current player
    if (this.gameState.validMoves.size === 0) {
      // Switch to other player
      this.gameState.currentPlayer = this.gameState.currentPlayer === BLACK ? WHITE : BLACK;
      this.updateValidMoves();

      // Other player also has no moves - game over
      if (this.gameState.validMoves.size === 0) {
        this.gameOver();
        return true;
      } else {
        // Pass turn
        const settings = this.getSettings();
        if (settings.autoPass) {
          const color = this.gameState.currentPlayer === BLACK ? 'Black' : 'White';
          this.showMessage(`No valid moves - passing to ${color}`);
        }
      }
    }

    return false;
  }

  private gameOver(): void {
    if (!this.gameState) {return;}

    this.gameState.isGameOver = true;

    let winner: string;
    let winnerClass: string;

    if (this.gameState.blackScore > this.gameState.whiteScore) {
      winner = t('game.blackWins');
      winnerClass = 'game-over-winner--black';
    } else if (this.gameState.whiteScore > this.gameState.blackScore) {
      winner = t('game.whiteWins');
      winnerClass = 'game-over-winner--white';
    } else {
      winner = t('game.tie');
      winnerClass = 'game-over-winner--tie';
    }

    const modal = document.getElementById('game-over-modal');
    if (modal) {
      modal.classList.remove('is-hidden');
      modal.innerHTML = `
        <h2 class="game-over-title">🏆 ${t('game.gameOverTitle')}</h2>
        <div class="final-scores">
          <p class="game-over-winner ${winnerClass}">
            ${winner}
          </p>
          <p>⚫ ${t('game.black')}: ${this.gameState.blackScore}</p>
          <p>⚪ ${t('game.white')}: ${this.gameState.whiteScore}</p>
          <p class="game-over-summary">
            ${t('game.totalMoves')}: ${this.gameState.moveHistory.length}
          </p>
        </div>
        <div class="modal-buttons">
          <button class="menu-button" id="play-again">${t('game.playAgain')}</button>
          <button class="menu-button" id="main-menu">${t('game.mainMenu')}</button>
        </div>
      `;

      document.getElementById('play-again')?.addEventListener('click', () => {
        modal.classList.add('is-hidden');
        this.startNewGame(this.gameState!.gameMode, this.gameState!.aiDifficulty);
      });

      document.getElementById('main-menu')?.addEventListener('click', () => {
        modal.classList.add('is-hidden');
        this.showMenu();
      });
    }
  }

  // Shared Health Bar Animation System
  private animateSharedHealthChange(player: 'black' | 'white', oldValue: number, newValue: number): void {
    const change = newValue - oldValue;
    const sharedHealthBar = document.querySelector('.shared-health-bar');

    // Add shake effect for big changes
    if (Math.abs(change) >= 5 && sharedHealthBar) {
      sharedHealthBar.classList.add('shake');
      setTimeout(() => sharedHealthBar.classList.remove('shake'), 300);
    }

    // Show damage/healing numbers
    if (change !== 0) {
      this.showSharedDamageNumber(player, change);
    }

    // Update will be handled by updateSharedHealthBar in updateScores
  }

  private showSharedDamageNumber(player: 'black' | 'white', change: number): void {
    const damageContainer = document.getElementById('damage-numbers');
    const playerSection = document.querySelector(`.player-section.${player === 'black' ? 'left' : 'right'}`);

    if (!damageContainer || !playerSection) {return;}

    const damageElement = document.createElement('div');
    damageElement.className = `damage-number ${change > 0 ? 'positive' : 'negative'}`;
    damageElement.textContent = change > 0 ? `+${change}` : change.toString();

    // Position the damage number above the player section
    const playerRect = playerSection.getBoundingClientRect();
    const containerRect = damageContainer.getBoundingClientRect();

    damageElement.style.left = `${playerRect.left - containerRect.left + playerRect.width / 2}px`;
    damageElement.style.top = `${playerRect.top - containerRect.top - 10}px`;
    damageElement.style.transform = 'translateX(-50%)';

    damageContainer.appendChild(damageElement);

    // Remove the element after animation
    setTimeout(() => {
      if (damageElement.parentNode) {
        damageElement.parentNode.removeChild(damageElement);
      }
    }, 1000);
  }

  // Legacy method for compatibility
  private showDamageNumber(player: 'black' | 'white', change: number): void {
    this.showSharedDamageNumber(player, change);
  }

  private updateSharedHealthBar(): void {
    if (!this.gameState) {return;}

    const blackFill = document.getElementById('black-fill');
    const whiteFill = document.getElementById('white-fill');
    const blackCount = document.getElementById('black-count');
    const whiteCount = document.getElementById('white-count');
    const turnIndicator = document.getElementById('current-turn-shared');

    if (!blackFill || !whiteFill || !blackCount || !whiteCount || !turnIndicator) {return;}

    const total = this.gameState.blackScore + this.gameState.whiteScore;
    const blackPercentage = total > 0 ? (this.gameState.blackScore / total) * 100 : 50;
    const whitePercentage = total > 0 ? (this.gameState.whiteScore / total) * 100 : 50;

    // Update shared health bar fills
    blackFill.style.width = `${blackPercentage}%`;
    whiteFill.style.width = `${whitePercentage}%`;

    // Update piece counts
    blackCount.textContent = this.gameState.blackScore.toString();
    whiteCount.textContent = this.gameState.whiteScore.toString();

    // Update turn indicator
    const currentPlayerName = this.gameState.currentPlayer === BLACK ? t('game.black') : t('game.white');
    turnIndicator.innerHTML = `<span>${currentPlayerName}</span>`;

    // Add special effects
    const blackFillElement = blackFill as HTMLElement;
    const whiteFillElement = whiteFill as HTMLElement;

    // Critical state (less than 15% of total pieces)
    if (blackPercentage < 15) {
      blackFillElement.classList.add('critical');
    } else {
      blackFillElement.classList.remove('critical');
    }

    if (whitePercentage < 15) {
      whiteFillElement.classList.add('critical');
    } else {
      whiteFillElement.classList.remove('critical');
    }

    // Dominant state (more than 75% of total pieces)
    if (blackPercentage > 75) {
      blackFillElement.classList.add('dominant');
      whiteFillElement.classList.remove('dominant');
    } else if (whitePercentage > 75) {
      whiteFillElement.classList.add('dominant');
      blackFillElement.classList.remove('dominant');
    } else {
      blackFillElement.classList.remove('dominant');
      whiteFillElement.classList.remove('dominant');
    }

    // Update VS divider position
    this.updateVSDividerPosition(blackPercentage);

    // Special effects for dramatic moments
    this.triggerSpecialHealthEffects(blackPercentage, whitePercentage);
  }

  private updateVSDividerPosition(blackPercentage: number): void {
    const vsDivider = document.getElementById('vs-divider');
    if (!vsDivider) return;

    // Position VS divider at the boundary between black and white areas
    vsDivider.style.left = `${blackPercentage}%`;
  }

  private triggerSpecialHealthEffects(blackPercentage: number, whitePercentage: number): void {
    const sharedHealthBar = document.querySelector('.shared-health-bar');
    const blackFill = document.getElementById('black-fill');
    const whiteFill = document.getElementById('white-fill');

    if (!sharedHealthBar || !blackFill || !whiteFill) return;

    // Remove all special effect classes first
    sharedHealthBar.classList.remove('balanced', 'dramatic-shift');
    blackFill.classList.remove('pulsing');
    whiteFill.classList.remove('pulsing');

    // Balanced state (45%-55% range)
    if (blackPercentage >= 45 && blackPercentage <= 55) {
      sharedHealthBar.classList.add('balanced');
    }

    // Dramatic shift detection (will be called when percentages change rapidly)
    const shift = Math.abs(blackPercentage - 50);
    if (shift > 35) {
      sharedHealthBar.classList.add('dramatic-shift');

      // Add pulsing effect to the dominant side
      if (blackPercentage > 65) {
        blackFill.classList.add('pulsing');
      } else if (whitePercentage > 65) {
        whiteFill.classList.add('pulsing');
      }

      // Remove dramatic shift effect after animation
      setTimeout(() => {
        sharedHealthBar.classList.remove('dramatic-shift');
      }, 1500);
    }
  }

  private updateDisplay(): void {
    if (!this.gameState) {return;}

    // Update shared health bar instead of old score display
    this.updateSharedHealthBar();

    // Update legacy elements (still needed for some old screens)
    const blackScore = document.getElementById('black-score');
    const whiteScore = document.getElementById('white-score');
    const currentTurn = document.getElementById('current-turn');

    if (blackScore) {blackScore.textContent = `⚫ ${t('game.black')}: ${this.gameState.blackScore}`;}
    if (whiteScore) {whiteScore.textContent = `⚪ ${t('game.white')}: ${this.gameState.whiteScore}`;}

    if (currentTurn) {
      const currentColor = this.gameState.currentPlayer === BLACK ? t('game.black') : t('game.white');
      const playerClass = this.gameState.currentPlayer === BLACK ? 'player-label player-label--black' : 'player-label player-label--white';
      currentTurn.innerHTML = `${t('game.current')}: <span class="${playerClass}">${currentColor}</span>`;
    }

    const turnIndicator = document.getElementById('turn-indicator');
    if (turnIndicator) {
      const currentColor = this.gameState.currentPlayer === BLACK ? t('game.black') : t('game.white');
      const playerClass = this.gameState.currentPlayer === BLACK ? 'player-label player-label--black' : 'player-label player-label--white';
      turnIndicator.innerHTML = `${t('game.currentTurn')} <span class="${playerClass}">${currentColor}</span>`;
    }

    // Render board
    this.renderBoard();
  }

  private showMessage(message: string): void {
    const aiStatus = document.getElementById('ai-status');
    if (aiStatus) {
      aiStatus.textContent = message;
      setTimeout(() => {
        aiStatus.textContent = '';
      }, 2000);
    }
  }

  private triggerScreenShake(grade: Grade): void {
    if (!this.container) {return;}

    const settings = this.getSettings();
    if (!settings.animations) {return;} // Skip if animations disabled

    let intensity = 0;
    let duration = 0;

    switch (grade) {
      case 'SSS':
        intensity = 8;  // 從20減少至8
        duration = 600;  // 從1200減少至600
        console.log('🌍 REALITY SHAKE - LEGENDARY!!!');
        break;
      case 'SS':
        intensity = 7;  // 從16減少至7
        duration = 550;  // 從1000減少至550
        console.log('🌪️ WORLD SHAKE - MYTHICAL!!');
        break;
      case 'S':
        intensity = 12;
        duration = 800;
        console.log('💥 SCREEN SHAKE - EPIC!');
        break;
      case 'A':
        intensity = 6;
        duration = 500;
        console.log('📳 Screen shake - Great!');
        break;
      case 'B':
        intensity = 3;
        duration = 300;
        console.log('📱 Screen shake - Good!');
        break;
      case 'C':
        // No shake for C grade
        return;
    }

    // Apply shake animation
    const app = this.container.querySelector('.super-reversi-app') as HTMLElement;
    if (app) {
      app.style.setProperty('--shake-intensity', `${intensity}px`);
      app.style.animation = `shake ${duration}ms ease-in-out`;

      // Remove animation after completion
      setTimeout(() => {
        app.style.animation = '';
        app.style.removeProperty('--shake-intensity');
      }, duration);
    }
  }

  // ===== MOBILE DETECTION =====

  /**
   * 檢測是否為移動設備
   */
  private isMobileDevice(): boolean {
    // Check user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));

    // Check screen size (mobile typically < 768px width)
    const isSmallScreen = window.innerWidth < 768;

    // Check for touch support
    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    return isMobileUA || (isSmallScreen && hasTouchSupport);
  }

  /**
   * 獲取適合設備的AI配置
   */
  private getDeviceOptimizedAIConfig(difficulty: AIDifficulty): { maxDepth: number; timeLimit: number; disableEndgameSolver: boolean } | null {
    const isMobile = this.isMobileDevice();

    if (!isMobile) {
      return null; // Use default configuration for desktop
    }

    // Mobile-optimized configurations
    const mobileConfigs: Partial<Record<AIDifficulty, { maxDepth: number; timeLimit: number; disableEndgameSolver: boolean }>> = {
      [AIDifficulty.MINIMAX_HARD]: {
        maxDepth: 6,
        timeLimit: 3000,
        disableEndgameSolver: false,
      },
      [AIDifficulty.ADVANCED]: {
        maxDepth: 6,
        timeLimit: 4000,
        disableEndgameSolver: false,
      },
      [AIDifficulty.MASTER]: {
        maxDepth: 6,
        timeLimit: 5000,
        disableEndgameSolver: true, // Disable endgame solver on mobile
      },
    };

    return mobileConfigs[difficulty] || null;
  }

  // ===== AI LOGIC =====

  private async makeAIMove(): Promise<void> {
    if (!this.gameState || this.gameState.validMoves.size === 0) {return;}

    // Prevent duplicate AI move requests
    if (this.aiThinking || this.isProcessingMove) {
      console.warn('🚫 AI move request blocked - already thinking or processing');
      return;
    }

    this.aiThinking = true;
    const aiStatus = document.getElementById('ai-status');

    // Always ensure AI thinking status is displayed, even for newly created elements
    if (aiStatus) {
      const isMobile = this.isMobileDevice();
      const isHighDifficulty = [AIDifficulty.ADVANCED, AIDifficulty.MASTER].includes(this.gameState.aiDifficulty);

      let thinkingText = 'AI 正在思考...';
      let additionalInfo = '';

      if (isMobile && isHighDifficulty) {
        thinkingText = 'AI 正在深度思考...';
        additionalInfo = '<div class="ai-additional-info ai-additional-info--muted">手機版建議使用較低難度以獲得更佳體驗</div>';
      }

      aiStatus.innerHTML = `
        <div class="ai-thinking-animation">
          <div class="thinking-dots">
            <div class="thinking-dot"></div>
            <div class="thinking-dot"></div>
            <div class="thinking-dot"></div>
          </div>
          <span>${thinkingText}</span>
          ${additionalInfo}
        </div>
      `;
    } else {
      console.warn('AI status element not found, but AI is thinking');
    }

    // Set up variables for timeout handling
    let warningTimer: NodeJS.Timeout | null = null;
    let emergencyTimer: NodeJS.Timeout | null = null;

    try {
      // Convert to core game state format
      const coreGameState = this.convertToCoreGameState(this.gameState);
      const aiStartTime = Date.now();
      const timeLimit = this.getAITimeLimit(this.gameState.aiDifficulty);

      // Create AI move request
      const request: AIMoveRequest = {
        gameState: coreGameState,
        player: this.gameState.currentPlayer === BLACK ? PlayerColor.BLACK : PlayerColor.WHITE,
        difficulty: this.gameState.aiDifficulty,
        timeLimit,
        priority: 5, // Normal priority
      };

      // Set up performance monitoring and timeout protection
      const isMobile = this.isMobileDevice();
      const warningTimeThreshold = isMobile ? 3000 : 8000; // Show warning after 3s on mobile, 8s on desktop
      const maxTimeThreshold = isMobile ? 15000 : 30000; // Force timeout after 15s on mobile, 30s on desktop

      warningTimer = setTimeout(() => {
        if (this.aiThinking && aiStatus) {
          aiStatus.innerHTML = `
            <div class="ai-thinking-animation">
              <div class="thinking-dots">
                <div class="thinking-dot"></div>
                <div class="thinking-dot"></div>
                <div class="thinking-dot"></div>
              </div>
              <span>AI 正在深度分析...</span>
              <div class="ai-additional-info ai-additional-info--warning">
                ⚡ ${isMobile ? '手機版處理較慢，請稍候' : '複雜局面需要更多時間'}
              </div>
            </div>
          `;
        }
      }, warningTimeThreshold);

      // Emergency timeout protection
      emergencyTimer = setTimeout(() => {
        if (this.aiThinking && !this.isProcessingMove) {
          console.warn('🚨 Emergency AI timeout triggered after', maxTimeThreshold, 'ms');
          if (warningTimer) clearTimeout(warningTimer);

          // Stop AI Manager thinking to prevent race condition
          if (defaultAIManager.isThinking()) {
            console.log('🛑 Stopping AI Manager before emergency fallback');
            defaultAIManager.stopThinking();
          }

          // Force stop AI and set processing flag to prevent conflicts
          this.aiThinking = false;
          this.isProcessingMove = true;

          if (aiStatus) {
            aiStatus.innerHTML = `
              <div class="ai-error-display">
                <div class="ai-error-title">
                  ⚠️ AI 超時保護啟動
                </div>
                <div class="ai-error-suggestion">
                  ${isMobile ? '自動降級為隨機移動' : '執行備用隨機策略'}
                </div>
              </div>
            `;
            setTimeout(() => {
              if (aiStatus) aiStatus.textContent = '';
            }, 3000);
          }

          // Make random fallback move directly without triggering AI
          const moves = Array.from(this.gameState!.validMoves);
          if (moves.length > 0) {
            const randomMove = moves[Math.floor(Math.random() * moves.length)]!;
            console.log('🎲 Emergency fallback to random move:', randomMove);

            // Use a small delay to ensure state is clean
            setTimeout(() => {
              this.makeMove(randomMove);
              this.isProcessingMove = false;
            }, 100);
          } else {
            this.isProcessingMove = false;
          }
        }
      }, maxTimeThreshold);

      // Request move from AI manager
      const result = await defaultAIManager.requestMove(request);
      if (warningTimer) clearTimeout(warningTimer);
      if (emergencyTimer) clearTimeout(emergencyTimer);

      // Performance tracking and logging
      const actualThinkingTime = Date.now() - aiStartTime;
      const thinkingTime = (result.thinkingTime / 1000).toFixed(1);
      const depth = result.depth || 0;
      const confidence = (result.confidence * 100).toFixed(0);
      const nodesEvaluated = result.nodesEvaluated || 0;

      // Log performance for debugging
      console.log(`🤖 AI Performance: ${thinkingTime}s (${this.gameState.aiDifficulty}), Depth: ${depth}, Mobile: ${isMobile}`);

      // Check if AI took longer than expected
      const wasSlowOnMobile = isMobile && actualThinkingTime > 5000;
      const wasSlowOnDesktop = !isMobile && actualThinkingTime > 15000;

      // Update AI status with thinking info
      if (aiStatus) {
        let performanceWarning = '';
        if (wasSlowOnMobile) {
          performanceWarning = '<div class="ai-additional-info ai-additional-info--performance">💡 考慮使用較低AI難度以加快遊戲速度</div>';
        } else if (wasSlowOnDesktop) {
          performanceWarning = '<div class="ai-additional-info ai-additional-info--performance">💡 複雜局面需要更多計算時間</div>';
        }

        aiStatus.innerHTML = `
          <div class="ai-info-display">
            <div class="ai-info-title">
              🤖 AI 計算完成
            </div>
            <div class="ai-info">
              <div>⏱️ ${thinkingTime}s</div>
              <div>🔍 深度 ${depth}</div>
              <div>🎯 ${confidence}%</div>
              ${nodesEvaluated > 0 ? `<div>🧮 ${nodesEvaluated.toLocaleString()} 節點</div>` : ''}
            </div>
            ${performanceWarning}
          </div>
        `;
      }

      // Add small delay for UI feedback
      setTimeout(() => {
        if (!this.isProcessingMove) { // Only proceed if not in emergency mode
          this.makeMove(result.position);

          if (aiStatus) {
            aiStatus.textContent = '';
          }
          this.aiThinking = false;
        } else {
          console.log('🚫 Skipping normal AI move completion - emergency mode active');
        }
      }, 300);

    } catch (error) {
      // Clear all timers in case of error
      if (warningTimer) clearTimeout(warningTimer);
      if (emergencyTimer) clearTimeout(emergencyTimer);
      console.error('AI move failed:', error);
      const isMobile = this.isMobileDevice();

      // Show error message with helpful suggestions
      if (aiStatus) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        const isTimeout = errorMsg.includes('timeout') || errorMsg.includes('時間');
        const errorMessage = isTimeout ? '思考超時' : '計算失敗';
        const suggestion = isMobile
          ? '建議降低AI難度或重新開始遊戲'
          : '請重試或降低AI難度';

        aiStatus.innerHTML = `
          <div class="ai-error-display">
            <div class="ai-error-title">
              ⚠️ AI ${errorMessage}
            </div>
            <div class="ai-error-suggestion">
              ${suggestion}
            </div>
          </div>
        `;

        // Clear error message after delay
        setTimeout(() => {
          if (aiStatus) {
            aiStatus.textContent = '';
          }
        }, 3000);
      }

      // Fallback to simple random move
      const moves = Array.from(this.gameState.validMoves);
      if (moves.length > 0) {
        const randomMove = moves[Math.floor(Math.random() * moves.length)]!;
        console.log('🎲 Falling back to random move due to AI error');
        this.makeMove(randomMove);
      }

      this.aiThinking = false;
    }
  }

  /**
   * Convert game state to core game state format
   */
  private convertToCoreGameState(gameState: GameState): CoreGameState {
    // Convert board to Uint8Array
    const board = new Uint8Array(64);
    for (let i = 0; i < 64; i++) {
      board[i] = gameState.board[i] === BLACK ? PlayerColor.BLACK :
                 gameState.board[i] === WHITE ? PlayerColor.WHITE :
                 PlayerColor.EMPTY;
    }

    // Convert valid moves to Map format
    const validMoves = new Map<number, readonly number[]>();
    for (const position of gameState.validMoves) {
      const flipped = this.getFlippedPieces(position, gameState.currentPlayer);
      validMoves.set(position, flipped);
    }

    return {
      board,
      currentPlayer: gameState.currentPlayer === BLACK ? PlayerColor.BLACK : PlayerColor.WHITE,
      validMoves,
      scores: {
        black: gameState.blackScore,
        white: gameState.whiteScore,
      },
      gameOver: gameState.isGameOver,
      consecutivePasses: gameState.consecutivePasses || 0,
    } as unknown as CoreGameState;
  }

  /**
   * Get AI time limit based on difficulty and device
   */
  private getAITimeLimit(difficulty: AIDifficulty): number {
    // Check for mobile-optimized configuration
    const mobileConfig = this.getDeviceOptimizedAIConfig(difficulty);
    if (mobileConfig && mobileConfig.timeLimit) {
      console.log(`📱 Using mobile-optimized timeout: ${mobileConfig.timeLimit}ms for ${difficulty}`);
      return mobileConfig.timeLimit;
    }

    // Default time settings for desktop
    const timeSettings = {
      [AIDifficulty.RANDOM]: 200,
      [AIDifficulty.GREEDY]: 1000,
      [AIDifficulty.MINIMAX_EASY]: 2000,
      [AIDifficulty.MINIMAX]: 3000,
      [AIDifficulty.MINIMAX_HARD]: 4000,
      [AIDifficulty.ADVANCED]: 5000,
      [AIDifficulty.MASTER]: 10000,
      [AIDifficulty.BLITZ]: 1500,
      [AIDifficulty.CUSTOM]: 5000,
    };

    return timeSettings[difficulty] || 3000;
  }

  // Helper functions for special effects detection
  private isEdgePosition(position: number): boolean {
    const row = Math.floor(position / 8);
    const col = position % 8;
    return (row === 0 || row === 7 || col === 0 || col === 7) &&
           !(position === 0 || position === 7 || position === 56 || position === 63);
  }

  private isChainMove(flippedPositions: number[]): boolean {
    if (!this.gameState || this.gameState.moveHistory.length === 0) {return false;}

    // Check if this move flips pieces that were recently placed/flipped
    const lastMove = this.gameState.moveHistory[this.gameState.moveHistory.length - 1];

    // If last move flipped a lot and this move also flips a lot, it's likely a chain
    return (lastMove as any)?.flipped?.length >= 3 && flippedPositions.length >= 3;
  }

  private isComebackMove(): boolean {
    if (!this.gameState) {return false;}

    const scoreDiff = this.gameState.currentPlayer === BLACK ?
      this.gameState.blackScore - this.gameState.whiteScore :
      this.gameState.whiteScore - this.gameState.blackScore;

    // Comeback if currently losing by 5+ pieces
    return scoreDiff <= -5;
  }

  private isDominationMove(): boolean {
    if (!this.gameState) {return false;}

    const scoreDiff = this.gameState.currentPlayer === BLACK ?
      this.gameState.blackScore - this.gameState.whiteScore :
      this.gameState.whiteScore - this.gameState.blackScore;

    // Domination if currently winning by 10+ pieces
    return scoreDiff >= 10;
  }

  // Simple Modal Methods
  private showSimpleConfirm(message: string, title?: string): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = document.getElementById('simple-modal');
      const modalTitle = document.getElementById('modal-title');
      const modalMessage = document.getElementById('modal-message');
      const confirmBtn = document.getElementById('modal-confirm');
      const cancelBtn = document.getElementById('modal-cancel');

      if (!modal || !modalTitle || !modalMessage || !confirmBtn || !cancelBtn) {
        // Fallback to native confirm
        resolve(confirm(message));
        return;
      }

      // Set modal content with i18n support
      modalTitle.textContent = title || t('modal.confirm.title');
      modalMessage.textContent = message;

      // Update button texts with current language
      confirmBtn.textContent = t('modal.confirm');
      cancelBtn.textContent = t('modal.cancel');

      // Show modal
      modal.classList.remove('is-hidden');

      // Handle confirm
      const handleConfirm = () => {
        modal.classList.add('is-hidden');
        confirmBtn.removeEventListener('click', handleConfirm);
        cancelBtn.removeEventListener('click', handleCancel);
        resolve(true);
      };

      // Handle cancel
      const handleCancel = () => {
        modal.classList.add('is-hidden');
        confirmBtn.removeEventListener('click', handleConfirm);
        cancelBtn.removeEventListener('click', handleCancel);
        resolve(false);
      };

      // Add event listeners
      confirmBtn.addEventListener('click', handleConfirm);
      cancelBtn.addEventListener('click', handleCancel);

      // Close on backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          handleCancel();
        }
      });
    });
  }
}

// Initialize application
document.addEventListener('DOMContentLoaded', async () => {
  console.log('DOM loaded - initializing Super Reversi');
  const container = document.getElementById('game-container');

  if (container) {
    try {
      const game = new SuperReversiGame();
      await game.initialize(container);

      // Store globally for debugging
      (window as any).superReversiGame = game;

      console.log('🎮 Game ready to play!');
    } catch (error) {
      console.error('Failed to initialize:', error);
      container.innerHTML = `
        <div class="error-screen">
          <h2>Error Loading Game</h2>
          <p>${error}</p>
        </div>
      `;
    }
  } else {
    console.error('Container not found!');
  }
});

export default SuperReversiGame;